import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from '@emotion/styled';
import { Spinner, TabBadge, Dialog } from '@/components/_common';
import Modal from '@/components/_common/Modal/Modal';
import { Tab, Button, Fade, Grid, Pagination, Typography, IconButton } from '@mui/material';
import axios from '@/_utils/axios';
import { Order, OrderJob } from '@/redux/slices/slice_types';
import { setModalOrderId } from '@/redux/slices/bulkSearchSlice';
import BulkSearchJob from './BulkSearchJob';
import { TabPanel, Tabs } from '@/components/_common/Tabs';
import BulkFailedJobs from './BulkFailedJobs';
import { WebSocketContextType, useWebsocketContext } from '@/contextAPI/websocketsContext';
import { useToast } from '@/components/_common/Toast';
import BulkTabContent, { BulkTabStatus } from './BulkTabContent';
import { PolarisIcon } from '@/exports/columnCommonComponents';
import { useAppDispatch } from '@/redux/hooks';
import AutoSubmitToggle from './AutoSubmitToggle';
import BulkModalProgressBar from './BulkModalProgressBar';
import BulkModalMetadata from './BulkModalMetadata';
import Checklist from '@/components/_common/Draw/Checklist';
import { applyFilter, clearFilters, setCurrentTable } from '@/redux/slices/dataTableSlice';
import { useHistory } from 'react-router-dom';

interface BulkSearchModalProps {
  open: boolean;
  onClose: () => void;
  id: number;
}

export type BulkJobError = {
  [key: string]: any;
  error?: string;
  line: number;
};

const PAGE_SIZE = 20; // Define the number of items per page
const TAB_CONFIG = [
  { label: 'Created', color: 'primary', statusKeys: ['created'] },
  { label: 'In Progress', color: 'warning', statusKeys: ['in_progress', 'submitting'] },
  { label: 'Needs Attention', color: 'pending', statusKeys: ['failed', 'in_error'] },
  { label: 'Cancelled', color: 'default', statusKeys: ['cancelled'] },
  { label: 'Errors', color: 'error', statusKeys: ['bulk_errors'] }
];

const defaultJobStats = {
  created: 0,
  in_progress: 0,
  submitting: 0,
  failed: 0,
  in_error: 0,
  cancelled: 0,
  bulk_errors: 0,
  requested_jobs: 0,
  total_jobs: 0
};

const BulkSearchModal = ({ open, onClose, id }: BulkSearchModalProps) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { success, error } = useToast();
  const [isOrderLoading, setIsOrderLoading] = useState(true);
  const [order, setOrder] = useState<Order>({} as Order);
  const [jobs, setJobs] = useState<Record<string, OrderJob>>({} as Record<string, OrderJob>);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tab, setTab] = useState(0);
  const [currentPage, setCurrentPage] = useState(1); // Current page state
  const [isJobStatsLoading, setIsJobStatsLoading] = useState(true); // Loading state for job stats
  const isCompleted = useMemo(() => order?.job_stats?.total_jobs + order?.job_stats?.bulk_errors >= order?.job_stats?.requested_jobs, [order]);
  const socketContext = useWebsocketContext();
  const { addMessageToSocket, removeMessageFromSocket } = socketContext as WebSocketContextType;
  const orderSocket = useMemo(() => (socketContext ? socketContext.sockets[`W${id}`] : undefined), [socketContext]);
  const modalOnMessageId = 'bulk_modal_message';
  const [jobStats, setJobStats] = useState<Order['job_stats'] | null>({ ...defaultJobStats, ...order?.job_stats });
  const [bulkErrors, setBulkErrors] = useState<BulkJobError[]>(order?.bulk_errors?.flat() || []);
  const [paginatedJobs, setPaginatedJobs] = useState<OrderJob[]>([]);
  const [showDialog, setShowDialog] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  useEffect(() => {
    if (open) {
      // get the first tab with a count
      const firstTabWithCount = getFirstTabWithCount();
      if (firstTabWithCount !== -1) {
        setTab(firstTabWithCount);
      }
      dispatch(setModalOrderId(id));
      fetchOrderAndJobs();
      if (orderSocket?.socket) {
        addMessageToSocket(`W${id}`, modalOnMessageId, (update) => {
          const data: { status: string; job?: OrderJob; errors?: BulkJobError[]; job_stats: Order['job_stats'] } = update.message.message;
          setJobStats(data.job_stats);
          if (data.job && data.status === 'job_setup_complete') {
            if (data.job.status === 'created') {
              setJobs((jobs) => ({ ...jobs, [data.job!.id]: data.job }));
            }
          } else if (data.errors && data.status === 'job_setup_error') {
            setBulkErrors((bulkError) => [...bulkError, ...(data as { errors: BulkJobError[] }).errors]);
          }
          // if data.job_stats.total_jobs + data.job_stats.bulk_errors === data.job_stats.requested_jobs,
          // then all jobs have been processed and we can close the websocket
          if (data.job_stats.total_jobs + data.job_stats.bulk_errors === data.job_stats.requested_jobs) {
            if (orderSocket?.socket) removeMessageFromSocket(String(id), modalOnMessageId);
            fetchOrderAndJobs();
          }
        });
      }
    }

    return () => {
      setBulkErrors([]);
      setJobs({});
      setTab(0);
      if (orderSocket?.socket) removeMessageFromSocket(String(id), modalOnMessageId);
    };
  }, [open]);

  // on older orders some of the job stats are missing, so we are merging with
  // defaultJobStats to ensure all keys are present
  useEffect(() => {
    if (order?.job_stats) {
      setJobStats({ ...defaultJobStats, ...order.job_stats });
    }
  }, [order]);

  const fetchOrderAndJobs = useCallback(() => {
    setIsOrderLoading(true);
    setIsJobStatsLoading(true); // Set job stats loading to true
    axios.get(`/core/orders/${id}/`).then((res) => {
      setOrder(res.data?.data);
      setJobStats(res.data?.data?.job_stats);
      setBulkErrors(res.data?.data?.bulk_errors?.flat());
      setIsJobStatsLoading(false); // Set job stats loading to false after data is fetched
    });

    fetchCreatedJobs();
  }, [bulkErrors, id]);

  const fetchCreatedJobs = useCallback(() => {
    axios
      .post(`/core/jobs/search/?page_num=0&page_size=3000`, {
        order_hid_filter: ['W' + id],
        status_filter: ['created']
      })
      .then((res) => {
        const data = res.data.data;
        const jobsObject: Record<string, OrderJob> = {};
        data.forEach((job: OrderJob) => (jobsObject[job.id] = job));
        setJobs(jobsObject);
      })
      .finally(() => setIsOrderLoading(false));
  }, [id]);

  const submitPendingBulkJobs = useCallback(() => {
    setIsSubmitting(true);
    axios({
      method: 'post',
      baseURL: '/api/v2/core/',
      url: `/orders/${id}/bulk-submit/`,
      data: {
        job_ids: Object.keys(jobs)
          .filter((key) => jobs[key].status === 'created')
          .map((key) => +key)
      }
    })
      .then(() => {
        success('Jobs submitted successfully');
        fetchOrderAndJobs();
      })
      .catch(() => error('Error submitting jobs'))
      .finally(() => setIsSubmitting(false));
  }, [fetchOrderAndJobs, id, jobs]);

  useEffect(() => {
    if (jobStats && tab >= 0) {
      const currentTabConfig = TAB_CONFIG[tab];
      const currentTabCount = currentTabConfig.statusKeys.reduce((acc, key) => acc + (jobStats[key] || 0), 0);
      if (currentTabCount === 0) {
        const firstTabWithCount = getFirstTabWithCount();
        if (firstTabWithCount !== -1) {
          setTab(firstTabWithCount);
        }
      }
    }
  }, [jobStats]);

  const handleTabChange = (_e: React.SyntheticEvent, newValue: number) => {
    setTab(newValue);
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  };

  const getFirstTabWithCount = () => {
    return TAB_CONFIG.findIndex((tabConfig) => {
      return tabConfig.statusKeys.some((statusKey) => ((jobStats || defaultJobStats)[statusKey] || 0) > 0);
    });
  };

  const areAllTabsHidden = () => {
    return TAB_CONFIG.every((tabConfig) => {
      return tabConfig.statusKeys.every((statusKey) => ((jobStats || defaultJobStats)[statusKey] || 0) === 0);
    });
  };

  const onViewAllJobsClick = () => {
    dispatch(setCurrentTable('jobs'));
    dispatch(clearFilters([]));
    const orderIdFilter = { type: 'filter_id', selections: [order.hid, 'order_hid_filter'] };
    dispatch(applyFilter(orderIdFilter));
    history.push(`/jobs`);
  };

  useEffect(() => {
    setPaginatedJobs(Object.values(jobs).slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE));
  }, [jobs, currentPage]);

  useEffect(() => {
    // if all the jobs on the last page are deleted, go back a page
    if (paginatedJobs.length === 0 && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  }, [paginatedJobs]);

  const handleCancelOrder = () => {
    setShowDialog(false);
    setIsCancelling(true);
    axios({
      method: 'post',
      baseURL: '/api/v2/core/',
      url: `/orders/${order.id}/cancel/`
    })
      .then(() => {
        success('Order cancelled successfully');
        onClose();
        dispatch(setModalOrderId(null));
      })
      .catch(() => error('Failed to cancel order'))
      .finally(() => setIsCancelling(false));
  };

  if (!open) return null;

  return (
    <StyledModal
      open={open}
      onClose={() => {
        onClose();
        dispatch(setModalOrderId(null));
      }}
      title={<div>Bulk Order {order?.hid ? order.hid : ''}</div>}
      icon={<PolarisIcon icon="bulk" />}
    >
      {isCancelling && <Spinner label="Canceling Order" />}

      <Dialog
        title="Cancel Order"
        titleIcon={<PolarisIcon icon="cancel" height={19} style={{ opacity: 0.5, marginRight: 8 }} />}
        open={showDialog}
        submitText="Cancel Order"
        cancelText="Close"
        buttonColor="error"
        onSubmit={handleCancelOrder}
        onCancel={() => setShowDialog(false)}
        maxWidth={400}
      >
        Are you sure you want to cancel order <strong>{order?.hid}</strong>?
      </Dialog>
      <Fade in timeout={800}>
        <div style={{ minHeight: 250 }}>
          {isOrderLoading && (
            <SpinnerWrapper style={{ position: 'absolute', top: '50%' }}>
              <Spinner />
            </SpinnerWrapper>
          )}
          <Grid container>
            {!isJobStatsLoading && (
              <BulkModalMetadata
                order={order}
                showCancelButton={jobStats?.bulk_errors >= jobStats?.requested_jobs && order.status === 'created'}
                onCancel={() => setShowDialog(true)}
              />
            )}
            <Grid item xs={12}>
              <BulkModalProgressBar total={jobStats?.requested_jobs} current={jobStats?.total_jobs + jobStats?.bulk_errors} />
            </Grid>
          </Grid>
          <ScrollableDiv>
            <div style={{ position: 'relative', minHeight: 50, marginBottom: 15 }}>
              <Tabs value={tab} onChange={handleTabChange} aria-label="bulk detail tabs" color={TAB_COLORS[tab]}>
                <StyledTab
                  color="primary"
                  label={<TabBadge label="Created" count={jobStats?.created} color="primary" />}
                  hidden={!jobStats?.created}
                />
                <StyledTab
                  color="warning"
                  label={<TabBadge label="In Progress" count={jobStats?.in_progress + jobStats?.submitting} color="warning" />}
                  hidden={!(jobStats?.in_progress + jobStats?.submitting)}
                />
                <StyledTab
                  color="pending"
                  label={<TabBadge label="Needs Attention" count={jobStats?.failed + jobStats?.in_error} color="pending" />}
                  hidden={!(jobStats?.failed + jobStats?.in_error)}
                />
                <StyledTab
                  color="default"
                  label={<TabBadge label="Cancelled" count={jobStats?.cancelled} color="default" />}
                  hidden={!jobStats?.cancelled}
                />
                <StyledTab
                  color="error"
                  label={<TabBadge label="Errors" count={jobStats?.bulk_errors} color="error" />}
                  hidden={!jobStats?.bulk_errors}
                />
              </Tabs>

              <TabActionButtons>
                <AutoSubmitToggle order={order} disabled={!jobStats?.created} setOrder={setOrder} />
                <IconButton onClick={fetchOrderAndJobs} disabled={isOrderLoading || isJobStatsLoading} sx={{ opacity: 0.6 }}>
                  <PolarisIcon icon="refresh" />
                </IconButton>
                {jobStats?.created > 1 && order.auto_submit_jobs && (
                  <Button
                    endIcon={<PolarisIcon icon="send" height={15} width={15} />}
                    disabled={isSubmitting}
                    onClick={submitPendingBulkJobs}
                    variant="contained"
                    sx={{ height: 35 }}
                  >
                    Submit {jobStats?.created} jobs
                  </Button>
                )}
              </TabActionButtons>
            </div>
            {/* if there are no tabs to display, display this */}
            {!isJobStatsLoading && areAllTabsHidden() && (
              <Fade in timeout={500}>
                {isCompleted ? (
                  <AllCompletedContainer>
                    <Checklist />
                    <Typography variant="h6" sx={{ mt: 3, textAlign: 'center' }}>
                      All Jobs Completed
                    </Typography>
                    <Button
                      onClick={onViewAllJobsClick}
                      variant="contained"
                      sx={{ mt: 2, whiteSpace: 'nowrap', minWidth: 125 }}
                      endIcon={<PolarisIcon icon="right_arrow" />}
                    >
                      View All Jobs
                    </Button>
                  </AllCompletedContainer>
                ) : (
                  <SpinnerWrapper>
                    <Spinner />
                  </SpinnerWrapper>
                )}
              </Fade>
            )}
            {/* Created */}
            <TabPanel value={tab} index={0}>
              {!isJobStatsLoading && (
                <div>
                  {paginatedJobs.map((job) => (
                    <BulkSearchJob orderId={order?.id} key={job?.id} job={job} updateCallback={fetchOrderAndJobs} submitting={isSubmitting} />
                  ))}
                  {paginatedJobs.length > 0 && jobStats?.created > PAGE_SIZE && (
                    <Grid container justifyContent="center" sx={{ mt: 3 }}>
                      <Pagination
                        count={Math.ceil(jobStats?.created / PAGE_SIZE)}
                        page={currentPage}
                        onChange={handlePageChange}
                        color="primary"
                        size="small"
                        disabled={isOrderLoading}
                      />
                    </Grid>
                  )}
                </div>
              )}
            </TabPanel>
            {/* In Progress */}
            <TabPanel value={tab} index={1} key={id}>
              <div>
                <BulkTabContent active={tab === 1} type={[BulkTabStatus.IN_PROGRESS, BulkTabStatus.SUBMITTING]} orderId={id} jobStats={jobStats} />
              </div>
            </TabPanel>
            {/* Needs Attention */}
            <TabPanel value={tab} index={2}>
              <BulkTabContent active={tab === 2} type={[BulkTabStatus.IN_ERROR, BulkTabStatus.FAILED]} orderId={id} jobStats={jobStats} />
            </TabPanel>
            {/* Cancelled */}
            <TabPanel value={tab} index={3}>
              <div>
                <BulkTabContent active={tab === 3} type={[BulkTabStatus.CANCELLED]} orderId={id} jobStats={jobStats} />
              </div>
            </TabPanel>
            {/* Failed */}
            <TabPanel value={tab} index={4}>
              <BulkFailedJobs failedJobs={bulkErrors} />
            </TabPanel>
          </ScrollableDiv>
        </div>
      </Fade>
    </StyledModal>
  );
};

export default BulkSearchModal;

const TAB_COLORS: ('primary' | 'warning' | 'success' | 'error' | 'default' | 'pending')[] = ['primary', 'warning', 'pending', 'default', 'error'];

const StyledTab = styled(Tab)<{ hidden?: boolean; color: 'primary' | 'warning' | 'success' | 'error' | 'default' | 'pending' }>`
  ${({ theme, color, hidden }) => `
    color: ${theme.palette[color].main};
    border-color:  ${theme.palette[color].main};
    visibility: ${hidden ? 'hidden' : 'visible'};
    width: ${hidden ? 0 : 'auto'};
    padding: ${hidden ? 0 : theme.spacing(0.5, 2)};
    &.Mui-selected {
      color: ${theme.palette[color].main};
    }
    
  `}
`;

const StyledModal = styled(Modal)`
  & > .MuiPaper-root {
    width: 95vw;
    display: flex;
    flex-direction: column;
    max-width: 2000px;
  }

  .MuiBox-root {
    overflow: hidden;
  }
`;

const TabActionButtons = styled.div`
  position: absolute;
  right: 0;
  top: ${({ theme }) => theme.spacing(-1)};
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
`;

const AllCompletedContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin-top: ${({ theme }) => theme.spacing(5)};
  position: relative;

  svg#checklist {
    height: 250px;
  }

  button {
    margin-bottom: ${({ theme }) => theme.spacing(4)};
  }
`;

const SpinnerWrapper = styled.div`
  position: relative;
  height: 250px;
  width: 100%;
  pointer-events: none;
`;

const ScrollableDiv = styled.div`
  overflow-y: auto;
  min-height: 200px;
  max-height: 50vh;
`;
